<template>
    <!-- eslint-disable vue/no-v-model-argument -->
    <l-map
        ref="mapRef"
        v-model:zoom="zoom"
        :center="[47.41322, -1.219482]"
        @ready="mapReady()"
    >
        <!-- eslint-enable vue/no-v-model-argument -->

        <!-- User Locations -->
        <l-feature-group
            ref="userLocationsRef"
            layerType="overlay"
            name="User Locations"
        >
            <l-marker-cluster-group
                v-if="settings.clusterOverlappingPlis"
                :options="clusterOptions"
            >
                <UserMapMarker
                    v-for="(location, id) in userLocations"
                    :key="id"
                    :userLocation="location"
                    :groups="groups"
                    :displayCallsign="settings.viewCallsignLabels"
                    @panToFitMarker="handlePanToFitMarker"
                />
            </l-marker-cluster-group>

            <UserMapMarker
                v-for="(location, id) in userLocations"
                v-else
                :key="id"
                :userLocation="location"
                :groups="groups"
                :displayCallsign="settings.viewCallsignLabels"
                @panToFitMarker="handlePanToFitMarker"
            />
        </l-feature-group>
        <div class="preload-user-icon">
            <img
                v-for="(preloadUrl, index) in preloadIcons"
                :key="index"
                :src="`/images/map/${preloadUrl}.svg`"
                rel="preload"
            />
        </div>

        <!-- Pins -->
        <l-feature-group
            layerType="overlay"
            name="Pins"
        >
            <l-marker
                v-for="(pin, id) in pins"
                :key="id"
                :lat-lng="pin.getLatLong()"
                :icon="pinIcons[pin.type]"
                :visible="pin.isVisible()"
            />
        </l-feature-group>

        <!-- Shapes -->
        <l-feature-group
            layerType="overlay"
            name="Shapes"
        >
            <div
                v-for="(shape, id) in shapes"
                :key="id"
            >
                <l-circle
                    v-if="shape.type === ShapeTypes.CIRCLE"
                    :lat-lng="[shape.points[0].latitude, shape.points[0].longitude]"
                    :radius="shape.points[0].radius"
                    :color="shape.color"
                    :fillColor="shape.color"
                    :fillOpacity="0.3"
                    :visible="shape.isVisible()"
                />

                <l-polyline
                    v-else-if="shape.type === ShapeTypes.LINE"
                    :lat-lngs="shape.getPolygonPoints()"
                    :color="shape.color"
                    :fill="false"
                    :visible="shape.isVisible()"
                />

                <l-polygon
                    v-else
                    :lat-lngs="shape.getPolygonPoints()"
                    :color="shape.color"
                    :fillColor="shape.color"
                    :fillOpacity="0.3"
                    :visible="shape.isVisible()"
                />
            </div>
        </l-feature-group>

        <l-tile-layer
            :url="currentMapType.url"
            :attribution="currentMapType.attribution"
            :minZoom="currentMapType.minZoom"
            :maxZoom="currentMapType.maxZoom"
        />

        <!-- Distance scale -->
        <l-control-scale position="bottomleft" />

        <!-- Full Screen toggle -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <fullscreen-toggle
                    :mapObj="mapObjectRef"
                    :mapContainer="mapContainer"
                />
            </div>
        </l-control>

        <!-- Clear map -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <a
                    class="map-button map-button--clear"
                    href="#"
                    title="Clear"
                    role="button"
                    aria-label="Clear"
                    @click.prevent="$emit('clearMapClicked')"
                />
            </div>
        </l-control>

        <!-- Center map on user locations -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <a
                    class="map-button map-button--center"
                    href="#"
                    title="Center"
                    role="button"
                    aria-label="Center"
                    @click.prevent="recenter"
                />
            </div>
        </l-control>

        <!-- Map types -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <div
                    v-click-outside="() => (mapTypesVisible = false)"
                    :class="{ active: mapTypesVisible }"
                    class="map-type-menu"
                >
                    <ul>
                        <li
                            v-for="(mapType, index) in mapTypes"
                            :key="index"
                            :class="{ active: index === currentMapTypeIndex }"
                            @click="currentMapTypeIndex = index"
                        >
                            <span>{{ mapType.name }}</span>
                        </li>
                    </ul>
                </div>
                <a
                    class="map-button map-button--layers"
                    href="#"
                    title="Layers"
                    role="button"
                    aria-label="Layers"
                    @click.stop.prevent="mapTypesVisible = true"
                />
            </div>
        </l-control>
    </l-map>
</template>

<script>
import { mapGetters } from 'vuex'
import L from 'leaflet'
import {
    LMap,
    LTileLayer,
    LCircle,
    LPolygon,
    LPolyline,
    LFeatureGroup,
    LMarker,
    LControl,
    LControlScale,
} from '@vue-leaflet/vue-leaflet'
import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'
import _ from 'lodash'
import { ShapeTypes } from '@/models/MapShape'
import FullscreenToggle from '@/components/Map/FullscreenToggle.vue'
import UserMapMarker from '@/components/Map/UserMapMarker.vue'
import 'vue-leaflet-markercluster/dist/style.css'
import 'leaflet/dist/leaflet.css'

export default {
    components: {
        LMap,
        LCircle,
        LPolygon,
        LPolyline,
        LTileLayer,
        LFeatureGroup,
        LMarker,
        FullscreenToggle,
        LControl,
        LControlScale,
        UserMapMarker,
        LMarkerClusterGroup,
    },

    filters: {
        localTime(value) {
            const utc = window.moment.unix(value)
            return window.moment.utc(utc).local().format('YYYY-MM-DD HH:mm:ss')
        },
    },

    props: {
        mapContainer: {
            type: undefined,
            required: true,
        },
        userLocations: {
            type: Array,
            required: true,
        },
        groups: {
            type: Array,
            required: true,
        },

        pins: {
            type: Array,
            required: true,
        },

        shapes: {
            type: Array,
            required: true,
        },
    },

    data() {
        // eslint-disable-next-line max-len
        const mapboxToken =
            'pk.eyJ1Ijoiam9zaG1ha2luZGEiLCJhIjoiY2ltYzVia2d6MDRid3U1a2ltZnBmemxnbCJ9.-4nVyK_cgBWxXEV1TNg9VQ'
        return {
            clusterOptions: {
                maxClusterRadius: 30,
                zoomToBoundsOnClick: false,
                spiderfyOnMaxZoom: false,
                iconCreateFunction: (cluster) =>
                    L.divIcon({
                        html: `<div>${cluster.getChildCount()}</div>`,
                        className: 'pli-cluster-custom',
                        iconSize: new L.Point(40, 40),
                    }),
                spiderfyDistanceMultiplier: 1.5,
            },
            mapObjectRef: null,
            userLocationsRef: null,
            userClustersRef: null,
            currentMapTypeIndex: 1,
            mapTypesVisible: false,
            mapTypes: [
                {
                    name: 'Streets',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/streets-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Topographical',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/outdoors-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Satellite',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Satellite + Streets',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
            ],
            zoom: 5,
            minZoom: 3,
            maxZoom: 18,
            url:
                'https://api.mapbox.com/styles/v1/mapbox/outdoors-v9/tiles/256/{z}/{x}/{y}' +
                `?access_token=${mapboxToken}`,
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            pinIcons: {
                default: L.icon({
                    iconUrl: '/images/map/pin-default.svg',
                    iconSize: [32, 32],
                    iconAnchor: [32 / 2, 32],
                }),
                emergency: L.icon({
                    iconUrl: '/images/map/pin-emergency.svg',
                    iconSize: [32, 32],
                }),
                warning: L.icon({
                    iconUrl: '/images/map/pin-warning.svg',
                    iconSize: [32, 32],
                }),
            },
            ShapeTypes,
        }
    },

    computed: {
        currentMapType() {
            return this.mapTypes[this.currentMapTypeIndex]
        },

        preloadIcons() {
            const preloadArray = []
            this.userLocations.forEach((user) => {
                if (user.isStale() && !_.includes(preloadArray, user.getIcon())) {
                    preloadArray.push(user.getIcon())
                } else if (!user.isStale() && !_.includes(preloadArray, `${user.getIcon()}-stale`)) {
                    preloadArray.push(`${user.getIcon()}-stale`)
                }
            })
            return preloadArray
        },

        ...mapGetters({
            settings: 'mission/mapSettings',
        }),
    },

    methods: {
        mapReady() {
            this.mapObjectRef = this.$refs.mapRef.leafletObject
            this.userLocationsRef = this.$refs.mapRef.leafletObject
            this.userClustersRef = this.$refs.userClusters

            this.$eventBus.on('centerOn', (data) => {
                let bounds = []
                if (!Array.isArray(data)) {
                    bounds = data.getAllLatLongs()
                } else {
                    for (let i = 0; i < data.length; i += 1) {
                        bounds.push(data[i].getLatLong())
                    }
                }
                this.mapObjectRef.flyToBounds(bounds)
            })

            this.createPosition('bottomleft-inner', 'leaflet-bottom leaflet-left-inner')
        },

        createPosition(label, className) {
            // Add bottomleft-inner position for controls
            const container = this.mapObjectRef._controlContainer
            const corners = this.mapObjectRef._controlCorners

            const div = document.createElement('div')
            div.className = className
            corners[label] = div
            container.appendChild(div)
        },

        recenter() {
            if (Object.keys(this.userLocationsRef._layers).length) {
                this.mapObjectRef.fitBounds(this.userLocationsRef.getBounds())
            }
        },

        handlePanToFitMarker(bounds, popupHeight) {
            const leftRightPadding = 120
            const mapControlsPaddingOffset = 50 // adjust for the leaflet controls
            const bottomPadding = popupHeight + mapControlsPaddingOffset

            this.mapObjectRef.panInside(bounds, {
                paddingTopLeft: [leftRightPadding, 30],
                paddingBottomRight: [leftRightPadding, bottomPadding],
            })
        },

        getPreloadIcon(user) {
            return user.isStale() && user.isInactive
                ? `/images/map/${user.getIcon()}.svg`
                : `/images/map/${user.getIcon()}-stale.svg`
        },
    },
}
</script>

<style scoped>
.preload-user-icon {
    display: none;
}

.preload-user-icon img {
    display: none;
}
</style>
